import { useEffect, useMemo, useState } from "react"
import { useLocation, useSearchParams } from "react-router-dom"
import axios from "axios"
import toast from "react-hot-toast"

import { Pagination } from "components"
import { useResultSet } from "repositories/db-repository"
import { getCurrentEntity } from "helpers/general-helper"

// 250726 (permintaan user): sumber (source) filter otomatis ikut ENTITAS user —
// user VISION hanya lihat/tambah VISION_ONE, ATALLA hanya ATALLA_HO. Backend sudah
// men-scope user ber-entity tunggal; ini menyelaraskan UI (default + kunci dropdown)
// supaya sumber entitas lain tidak bisa dipilih. null = entitas tak diketahui
// (super/multi) → bebas memilih.
const entitySourceValue = () => {
  const e = String(getCurrentEntity() || "").toUpperCase()
  if (e === "VISION") return "VISION_ONE"
  if (e === "ATALLA") return "ATALLA_HO"
  return null
}

// 210726 (permintaan user): UI Master Customer HO — data customer_master_mapping
// (import legacy "mst customer ho atalla"). Identitas (Kode/Nama/NPWP/Telp/Fax)
// bisa diedit (NPWP di file legacy kosong semua — diisi manual di sini); kolom
// saldo read-only. 220726 (permintaan user): tambah customer baru dari CMS
// (saldo mulai 0; user ber-entity otomatis ke sumber entity-nya di backend).

const MONEY_COLUMNS = [
  { key: "awal", label: "AWAL" },
  { key: "jual", label: "JUAL" },
  { key: "bayar", label: "BAYAR" },
  { key: "retur", label: "RETUR" },
  { key: "kredit", label: "KREDIT" },
  { key: "discount", label: "DISC" },
  { key: "dbnota", label: "DBNOTA" },
  { key: "krnota", label: "KRNOTA" },
  ...Array.from({ length: 12 }, (_, i) => ({ key: `p${i + 1}`, label: `P${i + 1}` })),
  { key: "disc4", label: "DISC4" },
  { key: "disc3", label: "DISC3" },
]

// Pagination FE: ubah URL API absolut dari paginator jadi URL halaman CMS
// (?page=N digabung param aktif) supaya klik nomor halaman tetap di SPA.
const toLocalPageLinks = (links, searchParams, pathname) => {
  if (!Array.isArray(links)) return links
  return links.map((l) => {
    if (!l?.url) return l
    let pageNum = null
    try { pageNum = new URL(l.url).searchParams.get("page") } catch { pageNum = null }
    if (!pageNum) return { ...l, url: null }
    const next = new URLSearchParams(searchParams)
    next.set("page", pageNum)
    return { ...l, url: `${pathname}?${next.toString()}` }
  })
}

const fmt = (v) => Number(v ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })

const apiBase = () => (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")

const inputClass =
  "w-full px-3 py-2 text-sm border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"

const FieldInput = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block mb-1 text-xs font-semibold text-neutral-500">{label}</label>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputClass}
    />
  </div>
)

// Modal bersama Tambah/Edit — mode edit menerima `row`, mode tambah `row` null
// tapi `isOpen` true. Edit mencakup Kode/Nama (220726); saldo tetap read-only.
function FormModal({ isOpen, row, onClose, onSaved }) {
  const isEdit = !!row
  const [kode, setKode] = useState("")
  const [name, setName] = useState("")
  const [alamat, setAlamat] = useState("")
  const [npwp, setNpwp] = useState("")
  const [telp, setTelp] = useState("")
  const [fax, setFax] = useState("")
  // 270726 (permintaan user): diskon default customer (%) — dipakai form Order HO
  // sebagai nilai awal diskon order.
  const [defaultDiscount, setDefaultDiscount] = useState("")
  const lockedSource = entitySourceValue()
  const [source, setSource] = useState(lockedSource ?? "ATALLA_HO")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setKode(row?.kode ?? "")
    setName(row?.name ?? "")
    setAlamat(row?.alamat ?? "")
    setNpwp(row?.npwp ?? "")
    setTelp(row?.telp ?? "")
    setFax(row?.fax ?? "")
    setDefaultDiscount(
      row?.default_discount_percent != null && Number(row.default_discount_percent) > 0
        ? String(Number(row.default_discount_percent))
        : ""
    )
    setSource(row?.source ?? lockedSource ?? "ATALLA_HO")
  }, [row, isOpen, lockedSource])

  if (!isOpen) return null

  const submit = async () => {
    if (!kode.trim() || !name.trim()) {
      toast.error("Kode dan Nama wajib diisi")
      return
    }

    const discountValue = Number(defaultDiscount || 0)
    if (!Number.isFinite(discountValue) || discountValue < 0 || discountValue > 100) {
      toast.error("Diskon default harus antara 0 dan 100 persen")
      return
    }

    setSaving(true)
    try {
      if (isEdit) {
        await axios.put(`${apiBase()}/customer-master/${row.yoiboi_customer_id}`, { kode, name, alamat, npwp, telp, fax, default_discount_percent: discountValue })
        toast.success("Master customer diperbarui")
      } else {
        await axios.post(`${apiBase()}/customer-master`, { kode, name, alamat, npwp, telp, fax, source, default_discount_percent: discountValue })
        toast.success("Customer master dibuat")
      }
      onSaved()
      onClose()
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Gagal menyimpan")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-full max-w-md p-6 bg-white rounded-2xl shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-semibold text-neutral-900">
          {isEdit ? "Edit Master Customer" : "Tambah Master Customer"}
        </div>
        {isEdit && (
          <div className="mt-1 text-xs text-neutral-500">
            {row.kode} — {row.name}
          </div>
        )}

        <div className="mt-4 space-y-3">
          {!isEdit && (
            <div>
              <label className="block mb-1 text-xs font-semibold text-neutral-500">Sumber</label>
              {/* User ber-entity tunggal: sumber dikunci ke entitasnya (backend juga
                  memaksa). Super/multi (lockedSource null) bebas memilih. */}
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                disabled={!!lockedSource}
                className={`${inputClass} disabled:bg-neutral-100 disabled:text-neutral-500`}
              >
                <option value="ATALLA_HO">Atalla</option>
                <option value="VISION_ONE">Vision</option>
              </select>
            </div>
          )}
          <FieldInput label="Kode *" value={kode} onChange={setKode} />
          <FieldInput label="Nama *" value={name} onChange={setName} />
          <FieldInput label="Alamat" value={alamat} onChange={setAlamat} placeholder="Belum diisi" />
          <FieldInput label="NPWP" value={npwp} onChange={setNpwp} placeholder="Belum diisi" />
          <FieldInput label="Telp" value={telp} onChange={setTelp} />
          <FieldInput label="Fax" value={fax} onChange={setFax} />
          <div>
            <FieldInput
              label="Diskon Default (%)"
              value={defaultDiscount}
              onChange={setDefaultDiscount}
              placeholder="0"
            />
            <p className="mt-1 text-[11px] text-neutral-500">
              Terisi otomatis sebagai diskon order saat customer ini dipilih di Order HO. Kosong = tanpa diskon.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-neutral-600 border border-neutral-200 rounded-xl"
          >
            Batal
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={submit}
            className="px-4 py-2 text-sm font-semibold text-white bg-brand-900 rounded-xl hover:bg-brand-800 disabled:opacity-60"
          >
            {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  // Sumber dikunci ke entitas user (VISION_ONE/ATALLA_HO). Super/multi (null) bebas.
  const lockedSourceValue = entitySourceValue()
  const sourceFilter = lockedSourceValue ?? (searchParams.get("source") || "")
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "50"
  const searchParam = searchParams.get("search") || ""

  const [search, setSearch] = useState(searchParam)
  const [editRow, setEditRow] = useState(null)
  const [showCreate, setShowCreate] = useState(false)

  useEffect(() => setSearch(searchParam), [searchParam])

  const { data: resultSet, isLoading, mutate } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    "customer-master",
    {
      search: searchParam || undefined,
      source: sourceFilter || undefined,
      page,
      per_page: perPage,
    }
  )

  const rows = useMemo(() => resultSet?.data ?? [], [resultSet])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)
    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })
    setSearchParams(nextParams)
  }

  const onSearchSubmit = () => mergeSearchParams({ search, page: "1" })

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Master Customer</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Master customer HO (import legacy) beserta saldo. Kode/Nama/NPWP/Telp/Fax bisa diedit.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 text-sm font-semibold text-white bg-brand-900 rounded-xl hover:bg-brand-800"
          >
            + Tambah Customer
          </button>

          <div className="min-w-[260px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Cari
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSearchSubmit() } }}
              placeholder="Kode / nama / telp / NPWP / alamat"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Sumber
            </label>
            {/* 210726: user ber-entity otomatis di-scope server (pilihan ini hanya efektif
                utk super/multi-entity — scope entity selalu menang di backend). */}
            <select
              value={sourceFilter}
              onChange={(e) => mergeSearchParams({ source: e.target.value, page: "1" })}
              disabled={!!lockedSourceValue}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800 disabled:bg-neutral-100 disabled:text-neutral-500"
            >
              {!lockedSourceValue && <option value="">Semua</option>}
              <option value="ATALLA_HO">Atalla</option>
              <option value="VISION_ONE">Vision</option>
            </select>
          </div>

          <div className="min-w-[120px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Per Halaman
            </label>
            <select
              value={perPage}
              onChange={(e) => mergeSearchParams({ per_page: e.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {[20, 50, 100].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm whitespace-nowrap">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Sumber</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Kode</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Nama</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Alamat</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">NPWP</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Telp</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Fax</th>
                {/* 270726: diskon default customer — dipakai Order HO. */}
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">DISKON %</th>
                {MONEY_COLUMNS.map((c) => (
                  <th key={c.key} className="px-4 py-3 font-semibold text-right text-neutral-700">{c.label}</th>
                ))}
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={30} className="px-4 py-10 text-center text-neutral-500">
                    Memuat data...
                  </td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={30} className="px-4 py-10 text-center text-neutral-500">
                    Tidak ada data master customer.
                  </td>
                </tr>
              )}

              {!isLoading &&
                rows.map((row) => (
                  <tr key={row.yoiboi_customer_id} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${String(row.source) === "VISION_ONE" ? "bg-purple-100 text-purple-700" : "bg-orange-100 text-orange-700"}`}>
                        {String(row.source) === "VISION_ONE" ? "VISION" : "ATALLA"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-neutral-800">{row.kode}</td>
                    <td className="px-4 py-3 text-neutral-800 max-w-[280px] whitespace-normal break-words leading-5">{row.name}</td>
                    <td className="px-4 py-3 text-neutral-600 max-w-[280px] whitespace-normal break-words leading-5">{row.alamat || "-"}</td>
                    <td className="px-4 py-3 text-neutral-600">{row.npwp || "-"}</td>
                    <td className="px-4 py-3 text-neutral-600">{row.telp || "-"}</td>
                    <td className="px-4 py-3 text-neutral-600">{row.fax || "-"}</td>
                    <td className="px-4 py-3 text-right text-neutral-800">
                      {Number(row.default_discount_percent ?? 0) > 0
                        ? `${Number(row.default_discount_percent)}%`
                        : "-"}
                    </td>
                    {MONEY_COLUMNS.map((c) => (
                      <td key={c.key} className="px-4 py-3 text-right text-neutral-800">{fmt(row[c.key])}</td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setEditRow(row)}
                        className="px-3 py-1.5 text-xs font-semibold text-white bg-brand-800 rounded-lg hover:bg-brand-600"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={toLocalPageLinks(resultSet?.links, searchParams, location.pathname)}
            from={resultSet?.from}
            to={resultSet?.to}
            total={resultSet?.total}
          />
        </div>
      </div>

      <FormModal
        isOpen={showCreate || !!editRow}
        row={editRow}
        onClose={() => { setEditRow(null); setShowCreate(false) }}
        onSaved={() => mutate()}
      />
    </div>
  )
}
